import type {
  TRichText,
  TRichTextLeaf,
  TRichTextNode
} from '@homepal/api-contract'

/**
 * Flattening rich text to one line. Needed wherever a rich value has to act
 * like a string again — a truncated cell, a tooltip title, a search key.
 */

const isLeaf = (node: TRichTextNode): node is TRichTextLeaf =>
  typeof (node as TRichTextLeaf).text === 'string'

/**
 * Marks split one sentence into several leaves — "en viktig sak" with a bold
 * middle word is three of them — so leaves concatenate raw. Element children
 * are separate blocks, and those need a space, or a bulleted list reads as
 * "förstaandra".
 */
const nodeText = (node: TRichTextNode): string => {
  if (isLeaf(node)) {
    return node.text
  }

  const children = Array.isArray(node.children) ? node.children : []

  return children.every(isLeaf)
    ? children.map(nodeText).join('')
    : children
      .map(nodeText)
      .filter((part) => part.length > 0)
      .join(' ')
}

/**
 * Rich text as a single line. Whitespace is collapsed and empty blocks drop
 * out, so a horizontal rule or a blank paragraph leaves no double space.
 *
 * Anything that is not an array flattens to '' — including a plain string
 * left behind by a row the migration never reached.
 */
export const richTextToPlainText = (
  value: TRichText | null | undefined
): string => {
  if (!Array.isArray(value)) {
    return typeof value === 'string' ? value : ''
  }

  return value
    .map((block) => nodeText(block).replace(/\s+/g, ' ').trim())
    .filter((text) => text.length > 0)
    .join(' ')
}
